// external
import type { FC } from 'react';

// interstate components
import { Box } from '@interstate/components/Box';
import { NumericInput } from '@interstate/components/NumericInput';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import { Typography } from '@interstate/components/Typography';
import { ResidualFieldsContainer, NumericInputContainer, SectionTitle } from './MileageCostOptions.style';

// types
import type { ResidualFormField } from './mileageCostOptionsTypes';

// utils
import { formatDollarsAndCents } from '@makemydeal/dr-common-utils';

export interface ResidualFormSectionProps {
    title: string;
    fields: ResidualFormField[];
}

/* istanbul ignore next -- sx callback for styling */
const getLabelSx = ({ tokens }: any) => ({ color: tokens.ComponentTextInputLabelColor });

export const ResidualFormSection: FC<ResidualFormSectionProps> = ({ title, fields }) => {
    const formatValue = (field: ResidualFormField): string => {
        const value = field.value;
        if (value === undefined || value === null || value === '') {
            return '—';
        }
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        if (isNaN(numValue)) {
            return '—';
        }
        if (field.inputPrefix === '$') {
            return formatDollarsAndCents(numValue);
        }
        if (field.inputPrefix === '%') {
            return `${numValue}%`;
        }
        return String(numValue);
    };

    const renderField = (field: ResidualFormField) => {
        const handleOnChange = (e: InterstateOnChangeEvent<string>) => {
            const value = String(e.target.value);
            /* istanbul ignore next -- optional chaining branch */
            field.onChange?.(field.key, value);
        };

        // For read-only fields, display as text (not input)
        if (field.readOnly) {
            // Empty cells render nothing
            const isEmptyCell = field.key.startsWith('emptyCell') && !field.label && !field.value;
            if (isEmptyCell) {
                return <NumericInputContainer key={field.key} data-testid={`residual-field-${field.key}`} />;
            }

            const displayValue = formatValue(field);

            // Read-only fields show label and value on the same line
            return (
                <NumericInputContainer key={field.key} data-testid={`residual-field-${field.key}`}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography tag="span" variant="label-md" sx={getLabelSx}>
                            {field.label}
                        </Typography>
                        <Typography tag="span" variant="body-md" sx={{ marginLeft: '1rem' }} data-testid={`${field.key}-value`}>
                            {displayValue}
                        </Typography>
                    </Box>
                </NumericInputContainer>
            );
        }

        // For editable fields, render input
        const commonProps = {
            id: `${field.key}-input`,
            name: `${field.key}-input`,
            value: field.value ?? '',
            hasError: !!field.error,
            errorMessage: field.error,
            disabled: field.disabled,
            'data-testid': `${field.key}-input`
        };

        const decimalMaxLength = field.allowDecimal ? (field.inputPrefix === '$' ? 2 : 5) : 0;
        const decimalMinLength = field.allowDecimal && field.inputPrefix === '$' ? 2 : 0;

        return (
            <NumericInputContainer key={field.key} data-testid={`residual-field-${field.key}`}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography tag="span" variant="label-md" sx={getLabelSx}>
                        {field.label}
                    </Typography>
                </Box>
                <NumericInput
                    {...commonProps}
                    inputPrefix={field.inputPrefix}
                    textAlign="left"
                    onChange={handleOnChange}
                    allowDecimal={field.allowDecimal}
                    decimalMaxLength={decimalMaxLength}
                    decimalMinLength={decimalMinLength}
                    autoInsertCommas={true}
                />
            </NumericInputContainer>
        );
    };

    return (
        <Box sx={{ marginBottom: '2rem' }}>
            <SectionTitle variant="h4" color="primary">
                {title}
            </SectionTitle>
            <ResidualFieldsContainer>{fields.map(renderField)}</ResidualFieldsContainer>
        </Box>
    );
};

export default ResidualFormSection;
